import * as React from "react";
import { Typography } from "antd";

export default function PrivacyPolicy() {
  return (
    <div className="bg-white max-w-screen-xl mx-auto m-4 p-8">
      <p>
        CarePulse is a service built and operated by Evalucom Consulting Ltd
        that allows stakeholders to share information with one another and the
        NHS London Purchased Healthcare Team.
      </p>
      <p>
        CarePulse e-PAS is a service built and operated by Evalucom Consulting
        Ltd that allows providers to submit applications for public tenders
        managed by the NHS London Purchased Healthcare Team.
      </p>
      <p>Our Privacy Notice explains:</p>
      <ul>
        <li>What information we collect and why we collect it.</li>
        <li>How we use that information.</li>
        <li>The options you have over the way we use information.</li>
      </ul>

      <Typography.Title level={3}>What information we collect</Typography.Title>
      <p>
        <strong>Professional contact information</strong> – when you register on
        CarePulse we collect your name, professional email address and telephone
        number, job title and organisation. This data is processed to allow us
        to deliver our services to you, and to ensure access to confidential
        information is controlled. The lawful basis for this processing is our
        legitimate interests, namely the proper administration of our services
        and the management of contracts between health care providers and
        commissioners.
      </p>
      <p>
        <strong>Usage data</strong> – CarePulse uses cookies (i.e. small text
        files placed on your computer when you first visit the site) to store
        information about your IP address, geographical location, browser type
        and version, operating system, referral source, length of visit, page
        views and website navigation paths, as well as information about the
        timing, frequency and pattern of your service use. This usage data is
        processed for the purposes of analysing the use of the website and
        services. The lawful basis for this is our legitimate interests, namely
        monitoring and improving our services.
      </p>
      <Typography.Title level={3}>
        Providing your personal data to others
      </Typography.Title>
      <p>
        We may share your contact information with NHS organisations and Local
        Authorities so that they can contact your organisation regarding the
        services you deliver. Your information is not shared with any other
        third parties unless required by law. We never sell your personal
        information.
      </p>

      <Typography.Title level={3}>
        How we protect your information
      </Typography.Title>
      <p>
        We believe that it is important for the personal information you provide
        to be used responsibly. As such, we have internal policies in place to
        protect your personal information from loss, misuse, alteration or
        unintentional destruction. Employees within our organisation who have
        access to your information have been trained to maintain the
        confidentiality of such information.
      </p>
      <p>
        We also understand the importance of protecting children's privacy in an
        online environment. Our sites are not intentionally designed for or
        directed at children under the age of 13 years. It is our policy never
        to knowingly collect or maintain information about anyone under the age
        of 13.
      </p>

      <Typography.Title level={3}>Your options</Typography.Title>
      <p>
        Your information is important in enabling us to provide you with the
        best user experience possible. However, it is also important to us that
        you are comfortable with our use of your personal information.
      </p>
      <p>
        Browsers now recognise when a cookie is offered and permit you to refuse
        or accept it. Alternatively, you may set your browser to block all
        cookies. If you are not sure whether your browser has this capability,
        you should check with the software manufacturer, your company's
        technology help desk or your Internet Service Provider.
      </p>
      <p>
        You can close your CarePulse user account at any time through your
        account page. We will maintain a record of your account for 30 days
        following closure of your account, following which we will delete any
        personal data we hold about you.
      </p>

      <Typography.Title level={3}>Your rights</Typography.Title>
      <p>
        In this Section we have summarised the rights that you have under the
        General Data Protection Regulation (GDPR) in relation to the personal
        data that we collect from you.
      </p>
      <p>
        You may exercise any of your rights in relation to your personal data by
        contacting us using the contact details at the bottom of this page.
      </p>
      <p>Your principal rights are:</p>
      <ol>
        <li>
          <strong>The right to acces</strong>
          <p>
            You have the right to obtain confirmation as to whether or not we
            hold your personal data and, where we do, to access to the personal
            data and obtain details of how we use it.
          </p>
        </li>
        <li>
          <strong>The right to rectification</strong>
          <p>
            You have the right to have any inaccurate personal data about you
            corrected and to have any incomplete personal data about you
            completed.
          </p>
        </li>
        <li>
          <strong>The right to erasure</strong>
          <p>
            You have the right for the personal data we hold about you to be
            deleted where it is no longer required for the purposes set out
            above. For details on how long we will keep your personal data,
            please see the paragraph “Data Retention”.
          </p>
        </li>
        <li>
          <strong>The right to restrict processing</strong>
          <div>
            In some circumstances you have the right to limit the way we use
            your data. These are where:
          </div>
          <ul>
            <li>you contest the accuracy of the personal data;</li>
            <li>
              we do not have a lawful basis to store your personal data but you
              do not want the information to be deleted;
            </li>
            <li>
              we no longer need your personal data but you require it in
              relation to a legal claim;
            </li>
            <li>
              you have objected to our use of your data and the objection is
              being verified.
            </li>
          </ul>
          <p>
            If processing has been restricted, we will stop using your personal
            data for the purposes above. However, we may continue to store the
            data. We will only continue to use the data: with your consent, in
            relation to a legal claim, for the protection of the rights of
            another person or if it is in the public interest.
          </p>
        </li>
        <li>
          <strong>The right to object to processing</strong>
          <p>
            You have the right to object to how we use your personal data, where
            the lawful basis for processing that information is for our
            legitimate interests. Unless there are compelling grounds to
            continue, we will stop processing your data when you make an
            objection.
          </p>
        </li>
        <li>
          <strong>The right to complain to a supervisory authority</strong>
          <p>
            If you consider that our processing of your personal information
            infringes data protection laws, you have a legal right to lodge a
            complaint with the Information Commissioner’s Office.
          </p>
        </li>
      </ol>

      <Typography.Title level={3}>Data retention</Typography.Title>
      <p>
        Your personal data is stored only as long as is necessary for us to
        carry out the above purposes.
      </p>
      <p>
        If your data is stored in relation to a contract between you and the NHS
        Clinical Commissioning Groups, Evalucom Consulting will hold a record of
        that information for 6 years following expiry of the contract.
      </p>
      <p>
        If your data is not stored in relation to a contract, your professional
        contact information will be deleted within 30 days following
        notification that you are no longer the appropriate contact for your
        organisation.
      </p>

      <Typography.Title level={4}>CarePulse e-PAS</Typography.Title>
      <p>
        If your data is collected in relation to the CarePulse e-PAS system,
        then if the outcome of your application is successful and you
        subsequently enter into a contract, Evalucom Consulting will hold a
        record of your information for 6 years following expiry of the contract.
      </p>
      <p>
        If your application is not successful, or you are successful but do not
        enter into a contract, or you do not submit your application, Evalucom
        Consulting will hold a record of your professional contact information
        for 1 year following the application deadline.
      </p>
      <Typography.Title level={3}>Cookies</Typography.Title>
      <p>
        We use cookies to enhance your user experience of CarePulse. We do so by
        using cookies to analyse how you interact with CarePulse. The table
        below provides information on the types of cookies we use and what they
        are used for.
      </p>

      <table>
        <thead>
          <th>Cookie</th>
          <th>What it is used for</th>
        </thead>
        <tbody>
          <tr>
            <td>csrftoken</td>
            <td>
              A randomly generated passphrase that acts as security protection
              to prevent other sites from making requests on your behalf
            </td>
          </tr>
          <tr>
            <td>sessionid</td>
            <td>
              Tracks an anonymous session to be able to handle data on a
              per-site-visitor basis, e.g. dismissing an announcement without a
              user account
            </td>
          </tr>
          <tr>
            <td>__utma</td>
            <td>Used to distinguish users and sessions</td>
          </tr>
          <tr>
            <td>__utmb & __utmc</td>
            <td>
              Used to determine new sessions and visits to CarePulse by users
              and their duration
            </td>
          </tr>
          <tr>
            <td>__utmt</td>
            <td>Used to throttle request rate</td>
          </tr>
          <tr>
            <td>__utmv</td>
            <td>Used to store visitor-level custom variable data</td>
          </tr>
          <tr>
            <td>__utmz</td>
            <td>Explains how users reach our site</td>
          </tr>
        </tbody>
      </table>
      <p />
      <Typography.Title level={3}>Changes</Typography.Title>
      <p>
        Our Privacy Notice may change from time to time. We will not reduce your
        rights under this Privacy Notice without your explicit consent. We will
        post any Privacy Notice changes on this page and, if the changes are
        significant, we will provide a more prominent notice (including, for
        certain services, email notification of Privacy Notice changes).
      </p>

      <Typography.Title level={3}>Contact us</Typography.Title>
      <p>
        If you have questions or comments about our administration of your
        personal information, please contact us at info@evalucom.co.uk. You may
        also use this address to communicate any concerns you may have regarding
        compliance with our Privacy Notice.
      </p>

      <p>Evalucom Consulting Ltd.</p>
      <p>VAT Registration No. 899541850</p>
      <p>Registered in England & Wales 6195102</p>
      <p>Registered Address: 20-22, Wenlock Road, London, England, N1 7GU</p>
    </div>
  );
}
